// Product catalog for the showcase build.
//
// The original shop sourced these from Shopify (gatsby-source-shopify). The store
// is closed, so the data is reconstructed from the prices and options that are
// hard-coded in the page components. Node ids match the ids the page queries filter on.

const base64 = s => (typeof Buffer !== "undefined" ? Buffer.from(s).toString("base64") : btoa(s))
const gid = (type, n) => base64(`gid://shopify/${type}/${n}`)

const DROPS_PER_MONTH = 62

let variantCounter = 40367800500000
const nextVariantId = () => gid("ProductVariant", variantCounter++)

let variantNodeCounter = 0

// compareAtPrice: undefined -> "0.00" (what the cart and Monatspaket header expect),
// null -> null (what the single product header expects)
function variant({ options, price, compareAtPrice, metafields = [], unitPrice = null, shopifyId }) {
  return {
    id: `variant-${variantNodeCounter++}`,
    shopifyId: shopifyId || nextVariantId(),
    title: options.length ? options.map(o => o.value).join(" / ") : "Default Title",
    selectedOptions: options.length ? options : [{ name: "Title", value: "Default Title" }],
    price: price.toFixed(2),
    compareAtPrice: compareAtPrice === undefined ? "0.00" : compareAtPrice === null ? null : compareAtPrice.toFixed(2),
    unitPrice: unitPrice === null ? null : { amount: unitPrice.toFixed(2) },
    metafields,
  }
}

function product({ id, legacyId, title, handle, description, images, options, variants }) {
  const prices = variants.map(v => parseFloat(v.price))
  return {
    id,
    shopifyId: gid("Product", legacyId),
    storefrontId: gid("Product", legacyId),
    legacyId: String(legacyId),
    title,
    handle,
    description,
    descriptionHtml: `<p>${description}</p>`,
    images: images.map((file, i) => ({ id: `${handle}-image-${i}`, originalSrc: `/showcase/${file}`, localFile: `showcase/${file}` })),
    options: options.length ? options : [{ name: "Title", values: ["Default Title"] }],
    priceRangeV2: {
      minVariantPrice: { amount: Math.min(...prices).toFixed(2) },
      maxVariantPrice: { amount: Math.max(...prices).toFixed(2) },
    },
    variants,
  }
}

const FLUORID = ["Mit Fluorid", "Ohne Fluorid"]

// Monatspaket: 3,95€ per person and month, 20% off for 4 months, 25% off for 6 months
const HOUSEHOLDS = ["1 Person", "2 Personen", "3 Personen", "4 Personen"]
const SUPPLIES = [
  { value: "2 Monate", months: 2, discount: 0 },
  { value: "4 Monate", months: 4, discount: 0.2 },
  { value: "6 Monate", months: 6, discount: 0.25 },
]
const MONTHLY_PRICE = 3.95

const monatspaketVariants = []
HOUSEHOLDS.forEach((household, h) => {
  const persons = h + 1
  SUPPLIES.forEach(supply => {
    FLUORID.forEach(fluorid => {
      const regular = MONTHLY_PRICE * supply.months * persons
      const price = Math.round(regular * (1 - supply.discount) * 100) / 100
      const unit = price / (DROPS_PER_MONTH * supply.months * persons)
      const isPreselected = persons === 1 && supply.value === "4 Monate" && fluorid === "Mit Fluorid"
      monatspaketVariants.push(variant({
        options: [
          { name: "Haushalt", value: household },
          { name: "Laufzeit", value: supply.value },
          { name: "Fluorid", value: fluorid },
        ],
        price,
        compareAtPrice: supply.discount > 0 ? regular : undefined,
        unitPrice: unit,
        metafields: [
          { id: "mf-drops", key: "drops", value: `${DROPS_PER_MONTH * supply.months * persons} Drops` },
          { id: "mf-months", key: "laufzeit", value: supply.value },
          { id: "mf-base", key: "grundpreis", value: `${unit.toFixed(2).replace(".", ",")}€ / Drop` },
        ],
        // the page preselects this exact variant id
        shopifyId: isPreselected ? "Z2lkOi8vc2hvcGlmeS9Qcm9kdWN0VmFyaWFudC80MDM2NzgwMDUxNjc5MQ==" : undefined,
      }))
    })
  })
})

const fluoridVariants = (price, compareAtPrice) =>
  FLUORID.map(value => variant({ options: [{ name: "Fluorid", value }], price, compareAtPrice }))

const products = [
  product({
    id: "6217d3ed-8025-54e8-a84b-bdea53a23b61",
    legacyId: 6619176370359,
    title: "Probierpaket",
    handle: "probierpaket",
    description: "20 Drops für 10 Tage, Bambuszahnbürste und Reisedose. Versandkostenfrei.",
    images: ["probierpaket-1.jpg", "probierpaket-2.png", "probierpaket-3.png", "probierpaket-4.jpg"],
    options: [{ name: "Fluorid", values: FLUORID }],
    variants: fluoridVariants(8.95, 11.95),
  }),
  product({
    id: "72c83be1-b5c8-5b3d-aa9a-d4c69816c66a",
    legacyId: 6899851854007,
    title: "Starter Kit",
    handle: "starterkit",
    description: "Zungenschaber, Zahnseide, Wechselzahnbürste, Reiseetui, Aufbewahrungstin und ein Glas mit 62 Drops.",
    images: ["starterkit-1.png", "starterkit-2.jpg", "starterkit-3.png"],
    options: [{ name: "Fluorid", values: FLUORID }],
    variants: fluoridVariants(19.95, null),
  }),
  product({
    id: "8d651639-2db7-5e3a-a3aa-1866bb0d9f53",
    legacyId: 7351701078250,
    title: "Drops Refill",
    handle: "monatspaket",
    description: "Toothpaste Drops im plastikfreien Nachfüllpack, 62 Drops pro Person und Monat.",
    images: ["monatspaket-1.png", "monatspaket-2.png", "monatspaket-3.png", "monatspaket-4.png", "monatspaket-5.jpg"],
    options: [
      { name: "Haushalt", values: HOUSEHOLDS },
      { name: "Laufzeit", values: SUPPLIES.map(s => s.value) },
      { name: "Fluorid", values: FLUORID },
    ],
    variants: monatspaketVariants,
  }),
  product({
    id: "46e3acb2-5819-530c-9958-2a4e07ba522e",
    legacyId: 7351700848874,
    title: "Zubehörbox",
    handle: "zubehoerbox",
    description: "Zahnseide, Zungenschaber, Zahnbürstenhalter, Reiseetui und Wechselzahnbürste.",
    images: ["zubehoerbox-1.jpg", "zubehoerbox-2.png", "zubehoerbox-3.png", "zubehoerbox-4.png", "zubehoerbox-5.png"],
    options: [],
    variants: [variant({ options: [], price: 12.95, compareAtPrice: null })],
  }),
  // containers selectable on the Monatspaket page (1,00€ each)
  product({
    id: "44cb8ac8-7e56-50ec-8cc6-60732bd03a8f",
    legacyId: 7351701110001,
    title: "Aufbewahrungstin",
    handle: "aufbewahrungstin",
    description: "Wiederverwendbare Dose aus Metall für deine Drops.",
    images: ["container-tin.png"],
    options: [],
    variants: [variant({ options: [], price: 1 })],
  }),
  product({
    id: "d57350dc-38d7-5ff7-acfa-cba3d018125f",
    legacyId: 7351701110002,
    title: "Glas",
    handle: "glas",
    description: "Wiederverwendbares Glas für deine Drops.",
    images: ["container-glas.png"],
    options: [],
    variants: [variant({ options: [], price: 1 })],
  }),
  // single accessories (cart slider and Zubehörbox)
  ...[
    ["5c8de43d-0bca-512a-ab7e-55ef35f6c8b2", 7351701120001, "Zahnseide", "zahnseide", 3.95],
    ["952c51df-47df-5ff8-ae0c-e412324ad325", 7351701120002, "Zungenschaber", "zungenschaber", 4.95],
    ["4342aea1-d207-59b0-a075-55c871b90bcc", 7351701120003, "Reiseetui", "reiseetui", 3.95],
    ["441b7ae2-5e6b-51e4-b27a-3f984ba023c0", 7351701120004, "Wechselzahnbürste", "wechselzahnbuerste", 2.95],
    ["ed5e00b9-487e-50ee-ba80-bd59a3bce9c6", 7351701120005, "Zahnbürstenhalter", "zahnbuerstenhalter", 2.95],
  ].map(([id, legacyId, title, handle, price]) => product({
    id,
    legacyId,
    title,
    handle,
    description: `${title}, plastikfrei.`,
    images: [`single-${handle}.png`],
    options: [],
    variants: [variant({ options: [], price })],
  })),
]

// flat lookup used by the local cart
const variantsById = {}
products.forEach(p => p.variants.forEach(v => {
  variantsById[v.shopifyId] = { product: p, variant: v }
}))

module.exports = { products, variantsById }
